'use client';

import React, { useEffect } from 'react';
import { Drawer } from 'antd';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import Link from 'next/link';
import ThemedButton from '../../common/buttons/Button';
import UserMenu from '../UserMenu';
import styles from './mobilemenu.module.scss';
import { useAppDispatch, useAppSelector } from '@/hooks/useRedux';
import { fetchProductMenu } from '@/redux/products/thunk';
import { MenuItem } from '@/redux/products/types';
import Image from 'next/image';
import { useAuthGuard } from '@/hooks/useAuthGuard';

interface MobileMenuProps {
  open: boolean;
  onClose: () => void;
  servicesOpen: boolean;
  toggleServices: () => void;
  isLoggedIn: boolean;
  username: string;
  logo: string;
  name: string;
}

const mobileNavLink =
  'block font-semibold text-[#1b2d2a] transition-colors hover:text-[#b01e2f]';

const MobileMenu: React.FC<MobileMenuProps> = ({
  open,
  onClose,
  servicesOpen,
  toggleServices,
  isLoggedIn,
  logo,
  name,
}) => {
  const dispatch = useAppDispatch();

  const {
    data: menuItems,
    status: menuStatus,
    error: menuError,
  } = useAppSelector((state) => state.products.menu);

  const { guard } = useAuthGuard({
    loginPath: '/account/signin',
    onRedirect: onClose,
  });

  useEffect(() => {
    if (menuStatus === 'idle') {
      dispatch(fetchProductMenu({}));
    }
  }, [dispatch, menuStatus]);

  const handleSubClick =
    (section: MenuItem, sub: MenuItem) =>
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      const href = `/services/application/${section.id}/i/${section.id}/${sub.id}`;

      if (guard(!!sub.requires_auth, href)) {
        e.preventDefault();
        return;
      }

      onClose();
    };

  return (
    <Drawer
      className={styles.customDrawer}
      placement="left"
      closable
      onClose={onClose}
      open={open}
      width="min(85vw, 340px)"
      title={
        <Link
          href="/"
          aria-label="Home"
          className="flex items-center"
          onClick={onClose}
        >
          <Image
            src={logo}
            alt={name}
            width={148}
            height={60}
            className="h-9 w-auto"
            priority
          />
        </Link>
      }
      styles={{
        body: { padding: 0, backgroundColor: '#fffcee' },
        header: { backgroundColor: '#fffcee' },
        footer: { backgroundColor: '#fffcee' },
      }}
    >
      <div className="bg-gray-100 p-3">
        {isLoggedIn ? (
          <div className="flex items-center justify-between">
            <UserMenu />
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2 [&_a]:block [&_button]:w-full">
            <ThemedButton
              theme="accent"
              text="Log In"
              href="/account/signin"
              loadingOnNavigate
            />
            <ThemedButton
              theme="secondary"
              text="Sign Up"
              loadingOnNavigate
              href="/account/signup"
            />
          </div>
        )}
      </div>
      <div className="space-y-4 px-2 py-2 text-xl text-[#1b2d2a]">
        <Link href="/" className={mobileNavLink} onClick={onClose}>
          Home
        </Link>

        <button
          onClick={toggleServices}
          className="flex w-full items-center justify-between font-semibold text-[#b01e2f]"
          aria-expanded={servicesOpen}
        >
          <span>Services</span>
          {servicesOpen ? (
            <FaChevronUp className="text-xs" />
          ) : (
            <FaChevronDown className="text-xs" />
          )}
        </button>

        {servicesOpen && (
          <div className="pl-4 space-y-4">
            {menuStatus === 'loading' ? (
              <p className="text-sm text-gray-400">Loading...</p>
            ) : menuStatus === 'failed' ? (
              <p className="text-sm text-red-500">{menuError}</p>
            ) : (
              menuItems.map((item: MenuItem) => (
                <div key={item.id}>
                  <h4 className="font-semibold">{item.name}</h4>
                  <ul className="mt-1 space-y-1 text-sm text-[#4a4a4a]">
                    {item.subproducts.map((sub) => (
                      <li key={sub.id}>
                        <Link
                          href={`/services/application/${item.id}/i/${item.id}/${sub.id}`}
                          onClick={handleSubClick(item, sub)}
                          className={`block transition-colors ${
                            sub.name === 'Betting & Gaming'
                              ? 'text-[#b01e2f] font-semibold'
                              : ''
                          }`}
                        >
                          {sub.name}
                          {sub.name === 'Betting & Gaming' && (
                            <span className="ml-1">&rarr;</span>
                          )}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))
            )}
          </div>
        )}

        <Link href="/support" className={mobileNavLink} onClick={onClose}>
          Help & Support
        </Link>
        <Link href="/contact" className={mobileNavLink} onClick={onClose}>
          Contact
        </Link>
        <Link
          href="/e-verify/documents"
          className={mobileNavLink}
          onClick={onClose}
        >
          E-Verify
        </Link>
      </div>
    </Drawer>
  );
};

export default MobileMenu;
